/**
 * アクションパネルコンポーネント
 *
 * 攻め・受け・なしボタン
 */

import React from 'react';
import { useGameStore } from '@stores/gameStore';
import { useUIStore } from '@stores/uiStore';
import type { ActionType } from '@/types/game';

export const ActionPanel: React.FC = () => {
  const { gameState, humanPlayerPosition, executeAction } = useGameStore();
  const { selectedPiece, setSelectedPiece } = useUIStore();

  const handleAction = (actionType: ActionType) => {
    if (!gameState) return;

    const action = {
      action_type: actionType,
      piece_type: actionType === 'PASS' ? null : selectedPiece,
      player_position: humanPlayerPosition,
    };

    executeAction(action);
    setSelectedPiece(null);
  };

  // バックエンドから取得した有効なアクション情報
  const validActions = gameState?.valid_actions;
  const isAwaitingHiddenPlace = validActions?.can_hidden_place ?? false;

  // ボタンの有効/無効判定（バックエンドのRuleEngineによる判定結果を使用）
  const canAttack = validActions ? (
    selectedPiece ? validActions.attack_pieces.includes(selectedPiece) : false
  ) : false;

  const canReceiveAction = validActions ? (
    selectedPiece ? validActions.receive_pieces.includes(selectedPiece) : false
  ) : false;

  const canPass = validActions?.can_pass ?? false;

  return (
    <div className="bg-white shadow px-4 py-6 rounded-lg text-black">
      <div className="flex items-center gap-4 mb-4">
        <h3 className="text-lg font-bold">アクション</h3>
        {isAwaitingHiddenPlace && (
          <p className="text-sm text-gray-600">手駒から1枚選択して伏せ駒を配置してください</p>
        )}
      </div>

      {isAwaitingHiddenPlace ? (
        /* 伏せ駒配置フェーズ */
        <div>
          <button
            onClick={() => handleAction('HIDDEN_PLACE')}
            disabled={!selectedPiece}
            className="bg-purple-500 text-white disabled:opacity-50 px-12 py-6 rounded text-2xl font-bold"
          >
            伏せ駒を配置
          </button>
        </div>
      ) : (
        /* 通常プレイフェーズ */
        <div className="flex gap-10">
          <button
            onClick={() => handleAction('ATTACK')}
            disabled={!canAttack}
            className="bg-red-500 text-white disabled:opacity-50 disabled:cursor-not-allowed px-12 py-6 rounded text-2xl font-bold"
          >
            攻め
          </button>
          <button
            onClick={() => handleAction('RECEIVE')}
            disabled={!canReceiveAction}
            className="bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed px-12 py-6 rounded text-2xl font-bold"
          >
            受け
          </button>
          <button
            onClick={() => handleAction('PASS')}
            disabled={!canPass}
            className="bg-gray-500 text-white disabled:opacity-50 disabled:cursor-not-allowed px-12 py-6 rounded text-2xl font-bold"
          >
            なし
          </button>
        </div>
      )}
    </div>
  );
};
